import { Card } from "@/components/Card";
import { useProfileLabel } from "@/lib/profileLabels";
import type { WilsonCI } from "@/lib/wilson";

interface AggregateProps {
  profileA: string;
  profileB: string;
  aBetter: number;
  bBetter: number;
  bothGood: number;
  bothBad: number;
  total: number;
  decided: number;
  winRateA: number | null;
  ciA: WilsonCI | null;
}

/**
 * Top-of-page win-rate card. Shows A's headline win rate over decided votes
 * with a Wilson 95% CI, plus a stacked-bar distribution across the four
 * outcomes with a colour + count legend below.
 */
export function Aggregate({
  profileA,
  profileB,
  aBetter,
  bBetter,
  bothGood,
  bothBad,
  total,
  decided,
  winRateA,
  ciA,
}: AggregateProps) {
  const pct = (n: number): number => (total ? (n / total) * 100 : 0);
  return (
    <Card style={{ padding: 22, marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          gap: 32,
          flexWrap: "wrap",
          alignItems: "baseline",
        }}
      >
        <div>
          <div className="overline">Headline · win rate of A</div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              marginTop: 6,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 38,
                fontWeight: 600,
                lineHeight: 1,
                color: "var(--atf-pink-700)",
              }}
            >
              {winRateA == null ? "—" : `${(winRateA * 100).toFixed(1)}%`}
            </div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color: "var(--muted-foreground)",
              }}
            >
              {ciA
                ? `95% CI ${(ciA.lower * 100).toFixed(1)}–${(ciA.upper * 100).toFixed(1)}%`
                : "—"}
            </div>
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--muted-foreground)",
              marginTop: 6,
              fontFamily: "var(--font-mono)",
            }}
          >
            over <b style={{ color: "var(--foreground)" }}>{decided}</b> decided / {total} total
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div className="overline" style={{ marginBottom: 8 }}>
            Verdict distribution
          </div>
          <StackedBar a={aBetter} b={bBetter} bg={bothGood} bb={bothBad} total={total} />
          <Legend
            profileA={profileA}
            profileB={profileB}
            aBetter={aBetter}
            bBetter={bBetter}
            bothGood={bothGood}
            bothBad={bothBad}
            pct={pct}
          />
        </div>
      </div>
    </Card>
  );
}

/**
 * Stacked horizontal bar encoding four verdict buckets. Colour alone would
 * fail colourblind readers, so segments wider than ~32 px render the raw
 * count inside the segment. The legend below is always the authoritative
 * readout.
 */
export function StackedBar({
  a,
  b,
  bg,
  bb,
  total,
}: {
  a: number;
  b: number;
  bg: number;
  bb: number;
  total: number;
}) {
  if (total === 0) {
    return (
      <div
        style={{
          height: 18,
          borderRadius: 6,
          border: "1px solid var(--border)",
          background: "var(--secondary)",
        }}
        aria-label="No votes yet"
      />
    );
  }
  type Seg = { v: number; color: string; label: string };
  const segs: Seg[] = [
    { v: a, color: "var(--atf-pink-600)", label: "A better" },
    {
      v: bg,
      color: "color-mix(in oklch, var(--success) 80%, white)",
      label: "Both good",
    },
    {
      v: bb,
      color: "color-mix(in oklch, var(--destructive) 55%, white)",
      label: "Both bad",
    },
    { v: b, color: "var(--atf-blue-950)", label: "B better" },
  ];
  return (
    <div
      style={{
        display: "flex",
        height: 18,
        borderRadius: 6,
        overflow: "hidden",
        border: "1px solid var(--border)",
        background: "var(--secondary)",
      }}
      role="img"
      aria-label={`Verdict distribution: ${a} A-better, ${bg} both-good, ${bb} both-bad, ${b} B-better`}
    >
      {segs.map((s, i) => {
        if (s.v <= 0) return null;
        const widthPct = (s.v / total) * 100;
        // Inline the count when the segment is wide enough to accommodate it.
        // At 18 px tall and a typical layout width of ~400+ px, segments over
        // ~8% → ~32px are reliably readable.
        const showCount = widthPct >= 8;
        return (
          <div
            key={i}
            style={{
              width: `${widthPct}%`,
              background: s.color,
              color: "white",
              fontSize: 10,
              fontFamily: "var(--font-mono)",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
            title={`${s.label}: ${s.v}`}
          >
            {showCount ? s.v : null}
          </div>
        );
      })}
    </div>
  );
}

function Legend({
  profileA,
  profileB,
  aBetter,
  bBetter,
  bothGood,
  bothBad,
  pct,
}: {
  profileA: string;
  profileB: string;
  aBetter: number;
  bBetter: number;
  bothGood: number;
  bothBad: number;
  pct: (n: number) => number;
}) {
  const label = useProfileLabel();
  return (
    <div
      style={{
        marginTop: 10,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 6,
      }}
    >
      <LegendRow
        swatch="var(--atf-pink-600)"
        label={`${label(profileA)} better`}
        count={aBetter}
        p={pct(aBetter)}
      />
      <LegendRow
        swatch="var(--atf-blue-950)"
        label={`${label(profileB)} better`}
        count={bBetter}
        p={pct(bBetter)}
      />
      <LegendRow
        swatch="color-mix(in oklch, var(--success) 80%, white)"
        label="Both good"
        count={bothGood}
        p={pct(bothGood)}
      />
      <LegendRow
        swatch="color-mix(in oklch, var(--destructive) 55%, white)"
        label="Both bad"
        count={bothBad}
        p={pct(bothBad)}
      />
    </div>
  );
}

function LegendRow({
  swatch,
  label,
  count,
  p,
}: {
  swatch: string;
  label: string;
  count: number;
  p: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: swatch,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          flex: 1,
          fontFamily: "var(--font-mono)",
          color: "var(--foreground)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          color: "var(--muted-foreground)",
        }}
      >
        {count} · {p.toFixed(0)}%
      </span>
    </div>
  );
}
